/**
 * iter318 wave 13 — VyomOS About page (Spec §11).
 * iter336 — CMS-driven when Super Admin has published "about" sections;
 * otherwise falls back to the static company narrative.
 */
import { Link } from 'react-router-dom';
import { useWebsiteContent } from '../../../hooks/useWebsiteContent';
import SectionRenderer from './SectionRenderer';

const VALUES = [
  ['Operators first', 'Every feature is validated with real academies and facility operators before it ships.'],
  ['Openness by design', '"Vyom" means sky and vastness — our platform gives your business the space to grow without hitting walls.'],
  ['Software with room to grow', 'Modular architecture that expands with new locations, programs, and workflows.'],
  ['Practical intelligence', 'AI that supports operators — surfacing insights, drafting communication, and speeding decisions.'],
];

const TIMELINE = [
  ['2023', 'Founded by academy operators tired of stitching together spreadsheets, payment tools, and messaging apps.'],
  ['2024', 'First 50 academies onboarded across three countries. Facility booking module launched.'],
  ['2025', 'Multi-location, white-label portals, and Coach billing shipped. AI insights layer introduced.'],
  ['2026', 'VyomOS becomes the operating system for growing academies and facilities.'],
];

export default function VyomosAbout() {
  const { content, loading } = useWebsiteContent('about');
  const cmsSections = content?.sections || [];
  if (loading && !content) {
    return <div className="min-h-[60vh]" data-testid="vyomos-about-loading" />;
  }
  if (cmsSections.length > 0) {
    return (
      <div data-testid="vyomos-about-cms">
        {cmsSections.map((s) => <SectionRenderer key={s.id} section={s} />)}
      </div>
    );
  }
  return <StaticAbout />;
}

export function StaticAbout() {
  return (
    <div data-testid="vyomos-about">
      {/* Hero */}
      <section className="bg-gradient-to-b from-[#EAF5FF] via-[#F8FBFF] to-white">
        <div className="max-w-6xl mx-auto px-6 py-20 md:py-24">
          <div className="text-xs uppercase tracking-[0.15em] font-semibold text-[#1677FF] mb-3">About VyomOS</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 max-w-3xl leading-[1.05]">
            Built by operators, for operators.
          </h1>
          <p className="text-lg text-[#566579] max-w-2xl">
            VyomOS was created to bring the operations of modern academies and facilities into a single, connected
            operating space — replacing the patchwork of tools that hold growing businesses back.
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="bg-white">
        <div className="max-w-6xl mx-auto px-6 py-16 grid md:grid-cols-2 gap-10 items-start">
          <div>
            <div className="text-xs uppercase tracking-[0.15em] font-semibold text-[#1677FF] mb-3">Our mission</div>
            <h2 className="text-3xl font-bold tracking-tight mb-4">Give growing businesses room to grow.</h2>
            <p className="text-[#566579] leading-relaxed mb-4">
              Academies, sports facilities, learning centers, and multi-program businesses operate at the intersection
              of scheduling, payments, communication, and community. Yet most run on tools built for other industries —
              or spreadsheets held together with duct tape.
            </p>
            <p className="text-[#566579] leading-relaxed">
              VyomOS is a single operating system that connects registrations, classes, coaches, facilities, payments,
              reporting, and communication. It grows with the business, adapts to how the business actually runs, and
              helps operators make better decisions faster.
            </p>
          </div>
          <div className="bg-[#F8FBFF] border border-[#DCE6F0] rounded-3xl p-8">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-[#1677FF] mb-4">By the numbers</h3>
            <div className="grid grid-cols-2 gap-6">
              {[
                ['200+', 'Academies and facilities'],
                ['3', 'Countries served'],
                ['1.2M+', 'Registrations processed'],
                ['99.95%', 'Platform uptime'],
              ].map(([n, l]) => (
                <div key={l}>
                  <div className="text-3xl font-bold text-[#10233F]">{n}</div>
                  <div className="text-sm text-[#566579]">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-[#EAF5FF]">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="text-xs uppercase tracking-[0.15em] font-semibold text-[#1677FF] mb-3">What we believe</div>
          <h2 className="text-3xl font-bold tracking-tight mb-8">Values that shape the product.</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {VALUES.map(([t, b]) => (
              <div key={t} className="bg-white border border-[#DCE6F0] rounded-2xl p-6">
                <h3 className="font-semibold text-lg mb-2">{t}</h3>
                <p className="text-sm text-[#566579] leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <div className="text-xs uppercase tracking-[0.15em] font-semibold text-[#1677FF] mb-3">Journey</div>
          <h2 className="text-3xl font-bold tracking-tight mb-10">From spreadsheet frustration to operating system.</h2>
          <div className="space-y-8">
            {TIMELINE.map(([year, body]) => (
              <div key={year} className="flex gap-6 items-start">
                <div className="w-16 flex-shrink-0">
                  <div className="text-2xl font-bold text-[#1677FF]">{year}</div>
                </div>
                <div className="flex-1 pb-2 border-l-2 border-[#EAF5FF] pl-6">
                  <p className="text-[#10233F] leading-relaxed">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#10233F] text-white">
        <div className="max-w-6xl mx-auto px-6 py-16 text-center">
          <h2 className="text-3xl font-bold mb-3">Ready to see it in action?</h2>
          <p className="text-[#B8CADD] mb-8 max-w-2xl mx-auto">
            Take VyomOS for a spin or set up a working session tailored to your operation.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <a href="https://demo.vyomos.io" className="px-6 py-3 rounded-full bg-[#1677FF] hover:bg-[#0e5fd6] text-white font-semibold" data-testid="about-cta-demo">
              Experience VyomOS
            </a>
            <Link to="/contactus" className="px-6 py-3 rounded-full border border-white/20 hover:border-white/60 text-white transition" data-testid="about-cta-contact">
              Book a Demo
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
